"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useActionInvoke = useActionInvoke;
// packages/shared-react/src/hooks/useActionInvoke.ts
const react_1 = require("react");
const recoil_1 = require("recoil");
const state_1 = require("../state");
const shared_1 = require("@heartcheck/shared");
/**
 * Best-effort detector: is the provided text a GraphQL document?
 */
function looksLikeGraphQL(doc) {
    if (!doc)
        return false;
    const s = doc.trim().toLowerCase();
    return s.startsWith("query ") || s.startsWith("mutation ");
}
/**
 * Hook used by AgentGraphQLBlock to invoke an action tied to a specific event.
 */
function useActionInvoke(chatId) {
    const [isInvoking, setIsInvoking] = (0, react_1.useState)(false);
    const eventsMap = (0, recoil_1.useRecoilValue)(state_1.ConversationEvents);
    const events = (0, react_1.useMemo)(() => (chatId ? eventsMap[chatId]?.value ?? [] : []), [chatId, eventsMap]);
    const findAgentIdForEvent = (0, react_1.useCallback)((eventId) => {
        if (!chatId || !eventId)
            return undefined;
        const ev = events.find((e) => e.id === eventId);
        return ev?.agentId || undefined;
    }, [chatId, events]);
    const invokeAction = (0, react_1.useCallback)(async (eventId, actionRequested) => {
        if (!chatId || !eventId || !actionRequested)
            return;
        const guardKey = `invoked-${eventId}`;
        if (localStorage.getItem(guardKey) === "1") {
            // eslint-disable-next-line no-console
            console.debug("[invokeAction] already invoked for", eventId);
            return;
        }
        const agentId = findAgentIdForEvent(eventId);
        try {
            setIsInvoking(true);
            if (looksLikeGraphQL(actionRequested)) {
                // Use Cognito User Pools (ID token) via the /graphql proxy
                const q = new shared_1.GraphqlQuery(actionRequested, {
                    forwardAuthMode: "userpools",
                });
                const anyQ = q;
                const run = anyQ.invoke ?? anyQ.execute ?? anyQ.run ?? anyQ.fetch;
                if (typeof run !== "function") {
                    throw new Error("GraphqlQuery missing invoke/execute/run/fetch.");
                }
                const result = await run.call(q, {});
                // eslint-disable-next-line no-console
                console.log("[invokeAction] GraphQL result", { agentId, eventId, result });
            }
            else {
                // eslint-disable-next-line no-console
                console.warn("[invokeAction] not GraphQL; skipping.", {
                    agentId,
                    eventId,
                    actionRequested,
                });
            }
            localStorage.setItem(guardKey, "1");
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error("❌ invokeAction failed:", err);
            throw err;
        }
        finally {
            setIsInvoking(false);
        }
    }, [chatId, findAgentIdForEvent]);
    return { isInvoking, invokeAction };
}
exports.default = useActionInvoke;
